/**
 * HousingCatalog.ts
 * 
 * Catalog presets and dynamic registration registry for Housing items.
 * Covers:
 * - 2. 바닥 (Floors): 흙, 짚, 잔디, 목재 마루, 석재, 대리석
 * - 3. 벽 (Walls): 통나무벽, 흙벽, 벽돌벽, 석조건물벽, 유리창틀벽, 나무문
 * - 4. 가구 (Furniture): 원목 침대, 고급 더블침대, 식탁, 원목 의자, 책장, 옷장, 벽난로
 * - 5. 소품 (Props/Decor): 와인잔, 책더미, 유리병, 양초, 황금 촛대, 액자(그림), 벽시계, 벨벳 러그, 모피 카페트
 * - 6. 외부인테리어 (Exterior): 기와지붕, 초가지붕, 굴뚝(연기), 처마등 랜턴, 정원 울타리, 정원 디딤돌, 석조 분수대, 꽃밭 화단
 * - 7. 외부시설 (Facilities): 논/밭타일, 과수목, 가축 우리(축사), 헛간, 🤵하우징 집사 NPC, 📮빨간색 우체통
 */

import { HousingCatalogItem, HousingTabType } from './HousingData';

class HousingCatalogRegistry {
  private items: Map<string, HousingCatalogItem> = new Map();

  constructor() {
    this.initBuiltinCatalog();
  }

  public register(item: HousingCatalogItem): void {
    const originalDraw = item.draw;
    // Wrap draw function to automatically apply rotation around (sx, sy)
    item.draw = (ctx: CanvasRenderingContext2D, sx: number, sy: number, size: number, rot: number = 0) => {
      if (rot && rot % 360 !== 0) {
        ctx.save();
        ctx.translate(sx, sy);
        ctx.rotate((rot * Math.PI) / 180);
        originalDraw(ctx, 0, 0, size, rot);
        ctx.restore();
      } else {
        originalDraw(ctx, sx, sy, size, 0);
      }
    };
    this.items.set(item.id, item);
  }

  public get(id: string): HousingCatalogItem | undefined {
    return this.items.get(id);
  }

  public getAll(): HousingCatalogItem[] {
    return Array.from(this.items.values());
  }

  public getByTab(tab: HousingTabType): HousingCatalogItem[] {
    return this.getAll().filter((item) => item.tab === tab);
  }

  private initBuiltinCatalog(): void {
    // ========================================================================
    // TAB 2: FLOORS (바닥재)
    // ========================================================================
    this.register({
      id: 'floor_dirt',
      name: '흙 바닥 (Dirt)',
      tab: 'floors',
      categoryLabel: '자연 바닥재',
      icon: '🟤',
      desc: '다져진 황토 흙 바닥입니다.',
      costCopper: 0,
      draw: (ctx, sx, sy, size) => {
        ctx.fillStyle = '#854d0e';
        ctx.fillRect(sx - size / 2, sy - size / 2, size, size);
        ctx.strokeStyle = '#713f12';
        ctx.strokeRect(sx - size / 2, sy - size / 2, size, size);
      },
    });

    this.register({
      id: 'floor_straw',
      name: '짚 바닥 (Straw)',
      tab: 'floors',
      categoryLabel: '자연 바닥재',
      icon: '🌾',
      desc: '푹신하게 엮은 건초 짚 바닥입니다.',
      costCopper: 0,
      draw: (ctx, sx, sy, size) => {
        ctx.fillStyle = '#ca8a04';
        ctx.fillRect(sx - size / 2, sy - size / 2, size, size);
        ctx.strokeStyle = '#a16207';
        ctx.lineWidth = 1;
        ctx.beginPath();
        ctx.moveTo(sx - size / 3, sy - size / 2);
        ctx.lineTo(sx + size / 4, sy + size / 2);
        ctx.stroke();
      },
    });

    this.register({
      id: 'floor_grass',
      name: '정원 잔디 (Grass)',
      tab: 'floors',
      categoryLabel: '자연 바닥재',
      icon: '🌱',
      desc: '푸르게 가꿔진 정원용 잔디 타일입니다.',
      costCopper: 0,
      draw: (ctx, sx, sy, size) => {
        ctx.fillStyle = '#15803d';
        ctx.fillRect(sx - size / 2, sy - size / 2, size, size);
        ctx.fillStyle = '#22c55e';
        ctx.fillRect(sx - size / 4, sy - size / 4, size / 2, size / 2);
      },
    });

    this.register({
      id: 'floor_wood_plank',
      name: '목재 마루 (Wood Plank)',
      tab: 'floors',
      categoryLabel: '실내 바닥재',
      icon: '🪵',
      desc: '아늑하고 따뜻한 가문비나무 마루판입니다.',
      costCopper: 0,
      draw: (ctx, sx, sy, size) => {
        ctx.fillStyle = '#b45309';
        ctx.fillRect(sx - size / 2, sy - size / 2, size, size);
        ctx.strokeStyle = '#78350f';
        ctx.lineWidth = 1;
        for (let i = -size / 2; i <= size / 2; i += size / 4) {
          ctx.beginPath();
          ctx.moveTo(sx - size / 2, sy + i);
          ctx.lineTo(sx + size / 2, sy + i);
          ctx.stroke();
        }
      },
    });

    this.register({
      id: 'floor_stone_tile',
      name: '석재 타일 (Stone)',
      tab: 'floors',
      categoryLabel: '실내외 바닥재',
      icon: '🪨',
      desc: '견고하고 단단하게 다듬은 화강암 석재 타일입니다.',
      costCopper: 0,
      draw: (ctx, sx, sy, size) => {
        ctx.fillStyle = '#475569';
        ctx.fillRect(sx - size / 2, sy - size / 2, size, size);
        ctx.strokeStyle = '#334155';
        ctx.strokeRect(sx - size / 2, sy - size / 2, size, size);
        ctx.strokeRect(sx - size / 4, sy - size / 4, size / 2, size / 2);
      },
    });

    this.register({
      id: 'floor_marble',
      name: '백대리석 (Marble)',
      tab: 'floors',
      categoryLabel: '고급 바닥재',
      icon: '🏛️',
      desc: '은은한 광택이 흐르는 왕궁풍 고급 대리석입니다.',
      costCopper: 0,
      draw: (ctx, sx, sy, size) => {
        ctx.fillStyle = '#f1f5f9';
        ctx.fillRect(sx - size / 2, sy - size / 2, size, size);
        ctx.strokeStyle = '#cbd5e1';
        ctx.strokeRect(sx - size / 2, sy - size / 2, size, size);
        ctx.strokeStyle = '#94a3b8';
        ctx.beginPath();
        ctx.moveTo(sx - size / 2, sy - size / 3);
        ctx.lineTo(sx + size / 3, sy + size / 2);
        ctx.stroke();
      },
    });

    // ⛰️ 1단 높이 지형 타일 (Tier 1 Elevation +12px)
    this.register({
      id: 'floor_elevation_1',
      name: '⛰️ 1단 높이 언덕 (Step 1)',
      tab: 'floors',
      categoryLabel: '고저차 지형',
      icon: '⛰️',
      desc: '지면보다 1단계(12px) 높게 솟아오른 입체 언덕/테라스 타일입니다.',
      costCopper: 0,
      draw: (ctx, sx, sy, size) => {
        const h = 8;
        // 남서/남동 입체 절벽면
        ctx.fillStyle = '#1e3a12';
        ctx.fillRect(sx - size / 2, sy - size / 2 + h, size, size - h);
        // 1단 높은 상단 초원면
        ctx.fillStyle = '#22c55e';
        ctx.fillRect(sx - size / 2, sy - size / 2 - h, size, size);
        ctx.strokeStyle = '#15803d';
        ctx.lineWidth = 1.5;
        ctx.strokeRect(sx - size / 2, sy - size / 2 - h, size, size);
        // 단차 층 표시 뱃지
        ctx.fillStyle = '#fef08a';
        ctx.font = 'bold 9px sans-serif';
        ctx.fillText('1단', sx - 8, sy - h + 3);
      },
    });

    // 🏔️ 2단 높이 지형 타일 (Tier 2 Elevation +24px)
    this.register({
      id: 'floor_elevation_2',
      name: '🏔️ 2단 높이 고원 (Step 2)',
      tab: 'floors',
      categoryLabel: '고저차 지형',
      icon: '🏔️',
      desc: '지면보다 2단계(24px) 높게 우뚝 솟은 고원/전망대 타일입니다.',
      costCopper: 0,
      draw: (ctx, sx, sy, size) => {
        const h = 16;
        // 깊은 입체 절벽 암벽면
        ctx.fillStyle = '#0f2008';
        ctx.fillRect(sx - size / 2, sy - size / 2 + h, size, size - h);
        // 2단 초고원 상단 초원면
        ctx.fillStyle = '#4ade80';
        ctx.fillRect(sx - size / 2, sy - size / 2 - h, size, size);
        ctx.strokeStyle = '#166534';
        ctx.lineWidth = 2;
        ctx.strokeRect(sx - size / 2, sy - size / 2 - h, size, size);
        // 단차 층 표시 뱃지
        ctx.fillStyle = '#ffffff';
        ctx.font = 'bold 9px sans-serif';
        ctx.fillText('2단', sx - 8, sy - h + 3);
      },
    });

    // ========================================================================
    // TAB 3: WALLS (벽체)
    // ========================================================================
    this.register({
      id: 'wall_log',
      name: '통나무 벽 (Log Wall)',
      tab: 'walls',
      categoryLabel: '목재 벽체',
      icon: '🪵',
      desc: '통나무를 차곡차곡 쌓아 올린 튼튼한 외벽입니다.',
      costCopper: 0,
      isObstacle: true,
      draw: (ctx, sx, sy, size) => {
        ctx.fillStyle = '#78350f';
        ctx.fillRect(sx - size / 2, sy - size / 2, size, size);
        ctx.fillStyle = '#92400e';
        ctx.fillRect(sx - size / 2 + 2, sy - size / 2 + 2, size - 4, size - 4);
        ctx.strokeStyle = '#451a03';
        ctx.strokeRect(sx - size / 2, sy - size / 2, size, size);
      },
    });

    this.register({
      id: 'wall_brick',
      name: '적벽돌 벽 (Brick Wall)',
      tab: 'walls',
      categoryLabel: '조적 벽체',
      icon: '🧱',
      desc: '단단하게 구워낸 붉은 벽돌 벽입니다.',
      costCopper: 0,
      isObstacle: true,
      draw: (ctx, sx, sy, size) => {
        ctx.fillStyle = '#991b1b';
        ctx.fillRect(sx - size / 2, sy - size / 2, size, size);
        ctx.strokeStyle = '#f87171';
        ctx.lineWidth = 1;
        ctx.strokeRect(sx - size / 2, sy - size / 2, size, size);
        ctx.beginPath();
        ctx.moveTo(sx - size / 2, sy);
        ctx.lineTo(sx + size / 2, sy);
        ctx.stroke();
      },
    });

    this.register({
      id: 'wall_castle_stone',
      name: '성벽 석조벽 (Castle Stone)',
      tab: 'walls',
      categoryLabel: '석조 벽체',
      icon: '🏰',
      desc: '마법과 풍파에도 끄떡없는 요새형 석조 성벽입니다.',
      costCopper: 0,
      isObstacle: true,
      draw: (ctx, sx, sy, size) => {
        ctx.fillStyle = '#334155';
        ctx.fillRect(sx - size / 2, sy - size / 2, size, size);
        ctx.strokeStyle = '#64748b';
        ctx.lineWidth = 2;
        ctx.strokeRect(sx - size / 2, sy - size / 2, size, size);
      },
    });

    this.register({
      id: 'wall_window',
      name: '유리창 벽 (Window Wall)',
      tab: 'walls',
      categoryLabel: '창호 벽체',
      icon: '🪟',
      desc: '채광이 좋은 목재 프레임 격자 유리창 벽입니다.',
      costCopper: 0,
      isObstacle: true,
      draw: (ctx, sx, sy, size) => {
        ctx.fillStyle = '#92400e';
        ctx.fillRect(sx - size / 2, sy - size / 2, size, size);
        ctx.fillStyle = '#38bdf8';
        ctx.fillRect(sx - size / 3, sy - size / 3, size / 1.5, size / 1.5);
        ctx.strokeStyle = '#ffffff';
        ctx.strokeRect(sx - size / 3, sy - size / 3, size / 1.5, size / 1.5);
      },
    });

    this.register({
      id: 'wall_wood_door',
      name: '목재 현관문 (Wood Door)',
      tab: 'walls',
      categoryLabel: '도어/문틀',
      icon: '🚪',
      desc: '여닫을 수 있는 아늑한 현관문입니다 (통과 가능).',
      costCopper: 0,
      isObstacle: false, // 문은 출입 가능
      draw: (ctx, sx, sy, size) => {
        ctx.fillStyle = '#b45309';
        ctx.fillRect(sx - size / 2, sy - size / 2, size, size);
        ctx.fillStyle = '#fbbf24';
        ctx.beginPath();
        ctx.arc(sx + size / 4, sy, 3, 0, Math.PI * 2);
        ctx.fill();
        ctx.strokeStyle = '#78350f';
        ctx.strokeRect(sx - size / 2, sy - size / 2, size, size);
      },
    });

    // ========================================================================
    // TAB 4: FURNITURE (가구)
    // ========================================================================
    this.register({
      id: 'furn_wood_bed',
      name: '원목 싱글 침대',
      tab: 'furniture',
      categoryLabel: '침실 가구',
      icon: '🛏️',
      desc: '편안한 숙면을 취할 수 있는 원목 프레임 침대입니다.',
      costCopper: 0,
      isObstacle: true,
      draw: (ctx, sx, sy, size) => {
        ctx.fillStyle = '#92400e';
        ctx.fillRect(sx - size / 2 + 2, sy - size / 2 + 2, size - 4, size - 4);
        ctx.fillStyle = '#f8fafc'; // 베개/이불
        ctx.fillRect(sx - size / 2 + 4, sy - size / 2 + 4, size - 8, size / 3);
        ctx.fillStyle = '#3b82f6'; // 침구
        ctx.fillRect(sx - size / 2 + 4, sy - size / 6, size - 8, size / 1.8);
      },
    });

    this.register({
      id: 'furn_double_bed',
      name: '왕실 더블 침대',
      tab: 'furniture',
      categoryLabel: '침실 가구',
      icon: '👑🛏️',
      desc: '고급 실크 침구가 씌워진 널찍한 더블 침대입니다.',
      costCopper: 0,
      isObstacle: true,
      draw: (ctx, sx, sy, size) => {
        ctx.fillStyle = '#7c2d12';
        ctx.fillRect(sx - size / 2, sy - size / 2, size, size);
        ctx.fillStyle = '#fef08a';
        ctx.fillRect(sx - size / 2 + 3, sy - size / 2 + 3, size - 6, size / 3);
        ctx.fillStyle = '#dc2626';
        ctx.fillRect(sx - size / 2 + 3, sy - size / 6, size - 6, size / 1.7);
      },
    });

    this.register({
      id: 'furn_wood_table',
      name: '원목 식탁 (Dining Table)',
      tab: 'furniture',
      categoryLabel: '거실/주방 가구',
      icon: '🪵🪑',
      desc: '가족 및 동료들과 둘러앉아 식사할 수 있는 튼튼한 테이블입니다.',
      costCopper: 0,
      isObstacle: true,
      draw: (ctx, sx, sy, size) => {
        ctx.fillStyle = '#b45309';
        ctx.fillRect(sx - size / 2 + 4, sy - size / 2 + 4, size - 8, size - 8);
        ctx.strokeStyle = '#78350f';
        ctx.lineWidth = 2;
        ctx.strokeRect(sx - size / 2 + 4, sy - size / 2 + 4, size - 8, size - 8);
      },
    });

    this.register({
      id: 'furn_wood_chair',
      name: '원목 의자 (Chair)',
      tab: 'furniture',
      categoryLabel: '거실/주방 가구',
      icon: '🪑',
      desc: '깔끔하게 다듬어진 등받이 의자입니다.',
      costCopper: 0,
      isObstacle: false,
      draw: (ctx, sx, sy, size) => {
        ctx.fillStyle = '#d97706';
        ctx.fillRect(sx - size / 4, sy - size / 4, size / 2, size / 2);
        ctx.fillStyle = '#78350f';
        ctx.fillRect(sx - size / 4, sy - size / 3, size / 2, 4);
      },
    });

    this.register({
      id: 'furn_bookshelf',
      name: '고서적 서재 책장 (Bookshelf)',
      tab: 'furniture',
      categoryLabel: '서재 가구',
      icon: '📚',
      desc: '마법서와 역사책이 가득 꽂혀 있는 고풍스러운 책장입니다.',
      costCopper: 0,
      isObstacle: true,
      draw: (ctx, sx, sy, size) => {
        ctx.fillStyle = '#78350f';
        ctx.fillRect(sx - size / 2 + 2, sy - size / 2 + 2, size - 4, size - 4);
        ctx.fillStyle = '#ef4444';
        ctx.fillRect(sx - size / 3, sy - size / 3, 5, size / 3);
        ctx.fillStyle = '#3b82f6';
        ctx.fillRect(sx - size / 3 + 7, sy - size / 3, 5, size / 3);
        ctx.fillStyle = '#10b981';
        ctx.fillRect(sx - size / 3 + 14, sy - size / 3, 5, size / 3);
      },
    });

    this.register({
      id: 'furn_fireplace',
      name: '석조 벽난로 (Fireplace)',
      tab: 'furniture',
      categoryLabel: '난방 가구',
      icon: '🔥',
      desc: '타닥타닥 타오르는 모닥불이 실내를 훈훈하게 덥혀줍니다.',
      costCopper: 0,
      isObstacle: true,
      draw: (ctx, sx, sy, size) => {
        ctx.fillStyle = '#475569';
        ctx.fillRect(sx - size / 2 + 2, sy - size / 2 + 2, size - 4, size - 4);
        ctx.fillStyle = '#0f172a';
        ctx.fillRect(sx - size / 3, sy - size / 4, size / 1.5, size / 2);
        ctx.fillStyle = '#ea580c';
        ctx.beginPath();
        ctx.arc(sx, sy, 6, 0, Math.PI * 2);
        ctx.fill();
      },
    });

    // ========================================================================
    // TAB 5: PROPS / DECOR (소품/장식)
    // ========================================================================
    this.register({
      id: 'prop_wine_glass',
      name: '유리 와인잔 (Wine Glass)',
      tab: 'props',
      categoryLabel: '테이블 소품',
      icon: '🍷',
      desc: '테이블 위에 올려놓는 투명한 와인잔입니다.',
      costCopper: 0,
      subGridSnap: true,
      draw: (ctx, sx, sy) => {
        ctx.fillStyle = '#ef4444';
        ctx.beginPath();
        ctx.arc(sx, sy - 3, 4, 0, Math.PI);
        ctx.fill();
        ctx.fillStyle = '#cbd5e1';
        ctx.fillRect(sx - 1, sy - 3, 2, 8);
        ctx.fillRect(sx - 3, sy + 5, 6, 2);
      },
    });

    this.register({
      id: 'prop_candlestick',
      name: '황금 촛대 (Candle)',
      tab: 'props',
      categoryLabel: '조명 소품',
      icon: '🕯️',
      desc: '따뜻한 촛불이 주변을 은은하게 비춰줍니다.',
      costCopper: 0,
      subGridSnap: true,
      draw: (ctx, sx, sy) => {
        ctx.fillStyle = '#eab308';
        ctx.fillRect(sx - 2, sy, 4, 8);
        ctx.fillStyle = '#f97316';
        ctx.beginPath();
        ctx.arc(sx, sy - 3, 3, 0, Math.PI * 2);
        ctx.fill();
      },
    });

    this.register({
      id: 'prop_painting_frame',
      name: '명화 액자 (Framed Art)',
      tab: 'props',
      categoryLabel: '벽걸이 장식',
      icon: '🖼️',
      desc: '솔라리스 왕국의 풍경을 담은 고급 액자입니다.',
      costCopper: 0,
      draw: (ctx, sx, sy, size) => {
        ctx.fillStyle = '#b45309';
        ctx.fillRect(sx - size / 3, sy - size / 3, size / 1.5, size / 1.8);
        ctx.fillStyle = '#38bdf8';
        ctx.fillRect(sx - size / 3 + 2, sy - size / 3 + 2, size / 1.5 - 4, size / 1.8 - 4);
      },
    });

    this.register({
      id: 'prop_velvet_rug',
      name: '붉은 벨벳 러그 (Velvet Rug)',
      tab: 'props',
      categoryLabel: '바닥 장식',
      icon: '🧶',
      desc: '고급스러운 감촉의 붉은 벨벳 카펫입니다.',
      costCopper: 0,
      draw: (ctx, sx, sy, size) => {
        ctx.fillStyle = '#991b1b';
        ctx.fillRect(sx - size / 2 + 2, sy - size / 2 + 2, size - 4, size - 4);
        ctx.strokeStyle = '#facc15';
        ctx.lineWidth = 1;
        ctx.strokeRect(sx - size / 2 + 4, sy - size / 2 + 4, size - 8, size - 8);
      },
    });

    // ========================================================================
    // TAB 6: EXTERIOR (외부 인테리어)
    // ========================================================================
    this.register({
      id: 'ext_clay_roof',
      name: '붉은 기와 지붕 (Clay Roof)',
      tab: 'exterior',
      categoryLabel: '지붕 자재',
      icon: '🏠',
      desc: '빗물을 튕겨내는 전통 양식의 붉은 점토 기와입니다.',
      costCopper: 0,
      draw: (ctx, sx, sy, size) => {
        ctx.fillStyle = '#b91c1c';
        ctx.beginPath();
        ctx.moveTo(sx, sy - size / 2);
        ctx.lineTo(sx + size / 2, sy + size / 2);
        ctx.lineTo(sx - size / 2, sy + size / 2);
        ctx.closePath();
        ctx.fill();
      },
    });

    this.register({
      id: 'ext_chimney',
      name: '석조 굴뚝 (Chimney)',
      tab: 'exterior',
      categoryLabel: '지붕 장식',
      icon: '🧱💨',
      desc: '지붕 위에 설치하여 연기를 배출하는 석조 굴뚝입니다.',
      costCopper: 0,
      isObstacle: true,
      draw: (ctx, sx, sy, size) => {
        ctx.fillStyle = '#475569';
        ctx.fillRect(sx - size / 4, sy - size / 2, size / 2, size / 1.5);
        ctx.fillStyle = '#94a3b8';
        ctx.beginPath();
        ctx.arc(sx, sy - size / 2 - 4, 3, 0, Math.PI * 2);
        ctx.fill();
      },
    });

    this.register({
      id: 'ext_wood_fence',
      name: '목재 정원 울타리 (Fence)',
      tab: 'exterior',
      categoryLabel: '정원 조경',
      icon: '🪵🚧',
      desc: '영지의 경계를 구획하는 정갈한 목재 울타리입니다.',
      costCopper: 0,
      isObstacle: true,
      draw: (ctx, sx, sy, size) => {
        ctx.fillStyle = '#b45309';
        ctx.fillRect(sx - size / 2, sy - 2, size, 4);
        ctx.fillRect(sx - size / 3, sy - size / 3, 4, size / 1.5);
        ctx.fillRect(sx + size / 3 - 4, sy - size / 3, 4, size / 1.5);
      },
    });

    this.register({
      id: 'ext_flower_garden',
      name: '화사한 꽃밭 (Flower Bed)',
      tab: 'exterior',
      categoryLabel: '정원 조경',
      icon: '💐',
      desc: '알록달록 아름다운 꽃들이 만개한 정원 화단입니다.',
      costCopper: 0,
      draw: (ctx, sx, sy, size) => {
        ctx.fillStyle = '#15803d';
        ctx.fillRect(sx - size / 2 + 2, sy - size / 2 + 2, size - 4, size - 4);
        ctx.fillStyle = '#f43f5e';
        ctx.beginPath();
        ctx.arc(sx - 4, sy - 4, 3, 0, Math.PI * 2);
        ctx.fill();
        ctx.fillStyle = '#facc15';
        ctx.beginPath();
        ctx.arc(sx + 4, sy + 4, 3, 0, Math.PI * 2);
        ctx.fill();
      },
    });

    this.register({
      id: 'ext_stone_fountain',
      name: '석조 분수대 (Fountain)',
      tab: 'exterior',
      categoryLabel: '정원 조경',
      icon: '⛲',
      desc: '시원한 물줄기가 솟구치는 석조 분수대 조형물입니다.',
      costCopper: 0,
      isObstacle: true,
      draw: (ctx, sx, sy, size) => {
        ctx.fillStyle = '#64748b';
        ctx.beginPath();
        ctx.arc(sx, sy, size / 2 - 2, 0, Math.PI * 2);
        ctx.fill();
        ctx.fillStyle = '#38bdf8';
        ctx.beginPath();
        ctx.arc(sx, sy, size / 3, 0, Math.PI * 2);
        ctx.fill();
      },
    });

    // ========================================================================
    // TAB 7: FACILITIES (외부 시설 / 농경 / 집사 / 우체통)
    // ========================================================================
    // 📮 필수 설치물: 빨간색 우체통 (Red Mailbox)
    this.register({
      id: 'facil_red_mailbox',
      name: '📮 빨간색 우체통 (Red Mailbox)',
      tab: 'facilities',
      categoryLabel: '필수 주택 인증 시설',
      icon: '📮',
      desc: '현관문 옆 필수 설치물! 집세(C/S/G/P) 납부 및 우편함을 이용합니다.',
      costCopper: 0,
      isObstacle: true,
      draw: (ctx, sx, sy, size) => {
        // Red mailbox post and box
        ctx.fillStyle = '#475569'; // 기둥
        ctx.fillRect(sx - 2, sy + 2, 4, size / 2 - 2);
        ctx.fillStyle = '#dc2626'; // 빨간색 우편함 본체
        ctx.fillRect(sx - size / 3, sy - size / 3, size / 1.5, size / 2.2);
        // Mail slot
        ctx.fillStyle = '#0f172a';
        ctx.fillRect(sx - size / 4, sy - size / 4, size / 2, 3);
        // Yellow flag
        ctx.fillStyle = '#facc15';
        ctx.fillRect(sx + size / 4, sy - size / 3, 4, 8);
      },
    });

    // 🌾 농경 논/밭 타일
    this.register({
      id: 'facil_farm_field',
      name: '경작용 농지/밭 (Farm Field)',
      tab: 'facilities',
      categoryLabel: '생산 시설',
      icon: '🌾🌱',
      desc: '작물을 파종하고 수확할 수 있는 비옥한 고랑 밭타일입니다.',
      costCopper: 0,
      draw: (ctx, sx, sy, size) => {
        ctx.fillStyle = '#713f12';
        ctx.fillRect(sx - size / 2, sy - size / 2, size, size);
        ctx.fillStyle = '#854d0e';
        for (let i = -size / 2 + 3; i < size / 2; i += 7) {
          ctx.fillRect(sx - size / 2, sy + i, size, 3);
        }
      },
    });

    // 헛간 및 가축 우리
    this.register({
      id: 'facil_animal_pen',
      name: '가축 우리/축사 (Livestock Pen)',
      tab: 'facilities',
      categoryLabel: '축산 시설',
      icon: '🐑🐄',
      desc: '양, 소, 닭 등 가축을 안전하게 사육하는 헛간 울타리입니다.',
      costCopper: 0,
      isObstacle: true,
      draw: (ctx, sx, sy, size) => {
        ctx.fillStyle = '#ca8a04';
        ctx.fillRect(sx - size / 2, sy - size / 2, size, size);
        ctx.strokeStyle = '#78350f';
        ctx.lineWidth = 2;
        ctx.strokeRect(sx - size / 2 + 2, sy - size / 2 + 2, size - 4, size - 4);
      },
    });

    // 🤵 하우징 전속 집사 NPC
    this.register({
      id: 'facil_butler_npc',
      name: '🤵 하우징 전속 집사 (Butler)',
      tab: 'facilities',
      categoryLabel: '영지 관리 NPC',
      icon: '🤵',
      desc: '주택 유지관리, 청소, 우체통 세금 대납 알림을 맡아주는 집사 NPC입니다.',
      costCopper: 0,
      isObstacle: true,
      draw: (ctx, sx, sy, size) => {
        // Butler Tuxedo rendering
        ctx.fillStyle = '#0f172a'; // Tuxedo
        ctx.fillRect(sx - size / 4, sy - size / 3, size / 2, size / 1.5);
        ctx.fillStyle = '#f8fafc'; // White shirt
        ctx.fillRect(sx - 3, sy - size / 3, 6, size / 3);
        ctx.fillStyle = '#dc2626'; // Red bow tie
        ctx.fillRect(sx - 4, sy - size / 3, 8, 3);
        ctx.fillStyle = '#fed7aa'; // Head
        ctx.beginPath();
        ctx.arc(sx, sy - size / 2 + 2, 5, 0, Math.PI * 2);
        ctx.fill();
      },
    });
  }
}

export const housingCatalog = new HousingCatalogRegistry();
